import { useCallback, useEffect, useState } from 'react';
import {
	ProbeDashboardApiHealth,
	ProbeResponse,
	ProbeSocketHealth,
	ProbeSyncHealth,
	SyncHealth,
} from '../../connection/health';
import { DEGRADED_LATENCY_MS, Probe, ProbeStatus } from '../../components/health-dashboard/types';

export interface ServiceHealth {
	key: string;
	label: string;
	probe: Probe<unknown>;
}

/** Any 2xx: the dashboard-API answers 203, so a 200-only check reports it down. */
const toProbe = (response?: ProbeResponse<unknown>): Probe<unknown> => {
	// No response is a failure, not a crash.
	if (!response) {
		return {
			status: ProbeStatus.Error,
			httpStatus: 0,
			latencyMs: 0,
			message: 'no response',
			data: null,
		};
	}

	const answered = response.httpStatus >= 200 && response.httpStatus < 300;

	/* eslint-disable-next-line no-nested-ternary */
	const status = !answered
		? ProbeStatus.Error
		: response.latencyMs > DEGRADED_LATENCY_MS
			? ProbeStatus.Degraded
			: ProbeStatus.Ok;

	return {
		status,
		httpStatus: response.httpStatus,
		latencyMs: response.latencyMs,
		message: response.error,
		data: response.body,
	};
};

/** Sync's 503 body names the failed dependency; a bare 2xx check loses it. */
const toSyncProbe = (response?: ProbeResponse<SyncHealth>): Probe<unknown> => {
	const probe = toProbe(response);
	const down = [
		...(response?.body?.redis.ok === false ? ['redis'] : []),
		...(response?.body?.socket.ok === false ? ['socket'] : []),
	];

	return down.length ? { ...probe, message: `${down.join(' and ')} unreachable` } : probe;
};

export interface UseServiceHealthResult {
	services: ServiceHealth[];
	refresh: () => void;
}

/** Process-level liveness: a service can be up while a room is broken. */
export default function useServiceHealth(
	intervalMs: number,
	/** False stops the polling entirely, for a tab nobody is looking at. */
	enabled = true,
): UseServiceHealthResult {
	const [services, setServices] = useState<ServiceHealth[]>([]);
	const [nonce, setNonce] = useState(0);

	const refresh = useCallback(() => setNonce((count) => count + 1), []);

	useEffect(() => {
		if (!enabled) return undefined;

		let cancelled = false;

		const check = () => Promise.all([
			ProbeDashboardApiHealth(),
			ProbeSocketHealth(),
			ProbeSyncHealth(),
		]).then(([api, socket, sync]) => {
			if (cancelled) return;
			setServices([
				{ key: 'dashboard-api', label: 'dashboard-API', probe: toProbe(api) },
				{ key: 'greenroom-socket', label: 'greenroom-socket', probe: toProbe(socket) },
				{ key: 'sync', label: 'sync', probe: toSyncProbe(sync) },
			]);
		});

		check();
		const timer = intervalMs ? setInterval(check, intervalMs) : undefined; // 0 pauses

		return () => {
			cancelled = true;
			if (timer) clearInterval(timer);
		};
	}, [intervalMs, nonce, enabled]);

	return { services, refresh };
}
